/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Saga to check params for Guard
 *
 * @author Yakov Gushchin
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */
import extend from 'lodash/extend';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import { LOAD_TABLE_META_SUCCESSFUL } from '@components/Table/constants/actionConstants';
import { LOAD_AUTH_DATA_SUCCESSFUL, LOGIN_PROCESS_SUCCESSFUL } from '@pages/PageAuth/constants/authConstants';

import { checkGuardError, checkGuardSuccessful } from '../actions/guardActions';
import { AccessComponentKeyEnum } from '../constants/guard/guardConstants';
import type { guardComponentType } from '../reducer/guardReducer';
import { checkGuardAuthUserDataSaga } from './checkGuardAuthUserDataSaga';
import { checkGuardFeatureSaga } from './checkGuardFeatureSaga';
import { checkGuardRoleSaga } from './checkGuardRoleSaga';
import { checkGuardUserTypeIdSaga } from './checkGuardUserTypeIdSaga';
import { checkGuardConditionsSaga } from './conditions/checkGuardConditionsSaga';

function* checkComponent(componentKey: AccessComponentKeyEnum): SagaIterator {
    let isAllowed: boolean;

    isAllowed = yield call(checkGuardRoleSaga, componentKey);

    // isAllowedByFeature
    if (isAllowed) {
        isAllowed = yield call(checkGuardFeatureSaga, componentKey);
    }

    if (isAllowed) {
        isAllowed = yield call(checkGuardAuthUserDataSaga, componentKey);
    }

    if (isAllowed) {
        isAllowed = yield call(checkGuardUserTypeIdSaga, componentKey);
    }

    // isAllowedByConditions
    if (isAllowed) {
        isAllowed = yield call(checkGuardConditionsSaga, componentKey);
    }

    return { [componentKey]: isAllowed };
}

export function* checkGuardSaga(): SagaIterator {
    try {
        const result = yield all(
            Object.values(AccessComponentKeyEnum).map((componentKey) => call(checkComponent, componentKey)),
        );
        const components = extend.apply({}, result) as guardComponentType;
        yield put(checkGuardSuccessful({ components: components }));
    } catch (error: any) {
        yield put(checkGuardError({ error }));
    }
}

export function* guardSagas(): SagaIterator {
    yield all([
        takeEvery([LOGIN_PROCESS_SUCCESSFUL, LOAD_AUTH_DATA_SUCCESSFUL, LOAD_TABLE_META_SUCCESSFUL], checkGuardSaga),
    ]);
}
